import { z } from 'zod';

/**
 * The wire shapes of the COMMON versioning pattern (refonte modulaire §4).
 *
 * Four objects share this pattern - criterion, persona, protocol, experience -
 * and it is posed once, on the criterion, the simplest of the four. What lives
 * here is only what is INDEPENDENT of an object's meaning: the two pointers,
 * the immutable versions, the server draft, the retirement, the history, the
 * usage. Everything that depends on the meaning of an object (its publication
 * refusals, its reference extractor, the check of its own references when it is
 * passed to production) belongs to the object's own module.
 *
 * ## Why the content-carrying shapes are BUILDERS and not schemas
 *
 * A version's `content` is the totality of one object at that version, and its
 * schema is the object's own. So every shape that carries a content is a
 * generic function of that schema - `versionedDetailSchema(criterionContentSchema)`
 * - rather than a schema with an `unknown` hole. The alternative was a
 * `z.unknown()` content parsed a second time by each caller, which is exactly
 * the kind of double validation the repo's boundary rule exists to avoid.
 *
 * ## Identifiers are English, labels are French
 *
 * The tag a route carries is `development`; the word an operator reads is
 * « développement ». The repo rule is code in English, screens in French, and a
 * stored enum is code: a base written with an accented key would be a base no
 * `z.enum` could narrow without a preprocess forever.
 */

/**
 * The two pointers, and the only two. A tag is what a reference names, never a
 * version number: the number appears only at the freeze of a session (§4).
 */
export const versionTagSchema = z.enum(['production', 'development']);
export type VersionTag = z.infer<typeof versionTagSchema>;
export const VERSION_TAGS = versionTagSchema.options;

/**
 * The kinds of versioned object. It grows by one entry per lot.
 *
 * `noyau-texts` is the fifth (issue #148): « Les textes des noyaux », a
 * SINGLETON (noyau.md §6.1, N8) that an experience cites by its tag alone
 * (`noyauTexts: { tag }`, N13) - the usage walk still keys it on `(kind, id,
 * tag)`, under the constant `NOYAU_TEXTS_ID`. It cites nothing itself.
 *
 * `models` and `rules` are the sixth and the seventh (vitrine.md section 5,
 * issue #192): « Les modèles » and « Les règles » of Réglages, two more
 * SINGLETONS at the patron of the texts of the noyaux. A session freezes the
 * version of each it plays; nothing cites them by a reference, so they carry
 * no « utilisé dans » of their own.
 *
 * `method` is the eighth (analyste.md §4, issue #196): « Méthode », a fourth
 * SINGLETON of the same patron - the words of the three functions and the six
 * styles the analyst reads. A session freezes its version only when its
 * experience plays a Noyau block (K13), and nothing cites it either.
 *
 * `npc` is the ninth (npc.md §3.4, issue #210): an NPC, the person a replay
 * plays, and the one kind with a DEVELOPMENT side only. It is deliberately
 * absent from `PROD_STATE_KINDS` and from `SYNC_FROZEN_KINDS` (N5): the factory
 * refuses any production, nothing syncs it, « État de la prod » never lists it,
 * and a session freezes none of its versions - a replay records the version it
 * played on its own row. Nothing cites an NPC either, so its usage stays empty.
 */
export const versionedKindSchema = z.enum([
  'criterion',
  'persona',
  'protocol',
  'experience',
  'noyau-texts',
  'models',
  'rules',
  'method',
  'npc',
]);
export type VersionedKind = z.infer<typeof versionedKindSchema>;

/**
 * The kinds that have a PRODUCTION side: every versioned kind but `npc` (npc.md
 * N5, issue #210), which lives on the development only. It is what the passage
 * to production, the synchronisation and « État de la prod » speak, so a kind
 * added to the versioned ones reaches them by a decision made HERE and never by
 * default. The tuple is spelled out for that reason, and `satisfies` keeps every
 * entry a real kind.
 */
export const PROMOTABLE_KINDS = [
  'criterion',
  'persona',
  'protocol',
  'experience',
  'noyau-texts',
  'models',
  'rules',
  'method',
] as const satisfies readonly VersionedKind[];
export const promotableKindSchema = z.enum(PROMOTABLE_KINDS);
export type PromotableKind = z.infer<typeof promotableKindSchema>;

/**
 * The version a reference points at (vitrine.md §3.1, V3, V16): a precise
 * version NUMBER, or `latest` - the pointer of the side the session plays on
 * (§3.2). `latest` is the default of every selector and of every fresh
 * reference; pinning a number is the explicit exception.
 */
export const versionPinSchema = z.union([z.number().int().positive(), z.literal('latest')]);
export type VersionPin = z.infer<typeof versionPinSchema>;

/** What a fresh reference points at (V16): `latest`, always. */
export const DEFAULT_VERSION_PIN: VersionPin = 'latest';

/**
 * A reference as it is STORED inside a content (vitrine.md §3.1): an entity id
 * and a pin - `latest`, or a precise version number - never a version id.
 *
 * It replaced the `{ id, tag }` of §4 of the refonte, which named a POINTER and
 * so could not say « this very version ». The migration of §3.5 rewrote every
 * stored `{ id, tag }` to `{ id, pin: 'latest' }`.
 */
export const versionedRefSchema = z
  .object({
    id: z.string().min(1),
    pin: versionPinSchema,
  })
  .strict();
export type VersionedRef = z.infer<typeof versionedRefSchema>;

/** The name the contract lot of issue #192 gave the same shape. */
export const pinnedRefSchema = versionedRefSchema;
export type PinnedRef = VersionedRef;

/**
 * The side a session plays on (vitrine.md §3.2, V18): `dev` is the default,
 * where every `latest` of the cascade is « latest dev »; `prod` makes every
 * `latest`, Modèles and Règles included, « latest prod ». On the vitrine there
 * is only one side, and it is `prod`. The side is frozen on the session with
 * the rest of the cascade.
 */
export const sessionSideSchema = z.enum(['dev', 'prod']);
export type SessionSide = z.infer<typeof sessionSideSchema>;
export const SESSION_SIDES = sessionSideSchema.options;

/**
 * Where a session (or a link, or an interview room) was BORN, as the atelier
 * sees it (vitrine.md section 8.2, issue #192): `dev`, the default and the only
 * value on the vitrine, or `prod` on a row the synchronisation brought up from
 * the vitrine. It is the mark « prod » of the list, and what makes such a row
 * READ-ONLY on the atelier: a session of the prod is never resumed there, and its
 * link or room never opens. Distinct from the SIDE (`sessionSideSchema`), which
 * is the pointers a session plays on: a session of the dev can play the side
 * `prod`, and it is still a session of the dev.
 */
export const sessionOriginSchema = z.enum(['dev', 'prod']);
export type SessionOrigin = z.infer<typeof sessionOriginSchema>;

/**
 * The pointer a side reads: `latest` on the `dev` side is the `development`
 * pointer (« latest dev »), on the `prod` side the `production` one (« latest
 * prod »). The one place the two vocabularies meet.
 */
export function tagOfSide(side: SessionSide): VersionTag {
  return side === 'prod' ? 'production' : 'development';
}

/** The side a pointer belongs to - the inverse of `tagOfSide`. */
export function sideOfTag(tag: VersionTag): SessionSide {
  return tag === 'production' ? 'prod' : 'dev';
}

/** A pin as a record key: `latest`, or the number as text. */
export function pinKey(pin: VersionPin): string {
  return pin === 'latest' ? 'latest' : String(pin);
}

/**
 * One line of the history: what a version IS, without its content.
 *
 * `tags` is where the two pointers sit right now, so a history line knows it is
 * the production one without the reader cross-checking two lists. It is a list
 * and not two booleans because both pointers land on one version often - a
 * freshly created entity has development on v1 and, once passed, production too.
 */
export const versionSummarySchema = z
  .object({
    id: z.string().min(1),
    number: z.number().int().positive(),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    note: z.string().nullable(),
    tags: z.array(versionTagSchema),
    /**
     * The mark « en prod » (vitrine.md §4.5, issue #192): the instant this version
     * was sent to the vitrine, or null. Never cleared. Optional on the wire, so a
     * reader that predates it - and every fixture - still reads a summary; absent
     * means not sent. Always null on the vitrine, which has one state.
     */
    sentToProdAt: z.string().nullable().optional(),
  })
  .strict();
export type VersionSummary = z.infer<typeof versionSummarySchema>;

/**
 * One referent of « utilisé dans » (§4): a protocol that cites the object, or an
 * experience that cites it through a protocol.
 *
 * Only the TAGGED versions of a referent count. A citation by a historical
 * version no pointer sits on is not a usage: no link can play it any more.
 * `via` carries the intermediary when the referent is reached through another
 * object (« Expérience Accueil PMS par Entretien fondateur »).
 */
export const versionedUsageSchema = z
  .object({
    kind: versionedKindSchema,
    id: z.string().min(1),
    name: z.string().min(1),
    tags: z.array(versionTagSchema).min(1),
    via: z.string().nullable(),
  })
  .strict();
export type VersionedUsage = z.infer<typeof versionedUsageSchema>;

/**
 * The kinds a « utilisé dans » LINE can carry: the five versioned ones (the
 * texts of the noyaux joined the four in #148), plus `link` (§4, H25).
 *
 * A link is NOT a versioned object - no version, no pointer, no draft - and
 * `versionedKindSchema` must keep saying so: it is what `usage-sources.ts` is
 * keyed on and what a reference inside a content points at. But §4 asks for a
 * usage kind `link` « à côté des sortes versionnées, avec la même mécanique et
 * le même affichage », because the referents of an EXPERIENCE are the active
 * links of §5.1 and nothing else.
 *
 * So the widening is on the LINE and on nothing else. The detail builder below
 * and the generic readers of the frontend speak the line type for every
 * versioned object - one type for one panel, since the layer draws every detail through
 * the same components - while only an experience can actually receive a link
 * line: a link cites an experience, and the second hop of the walk is refused
 * to it, so a criterion never sees one at runtime.
 */
export const usageKindSchema = z.enum([...versionedKindSchema.options, 'link'] as const);
export type UsageKind = z.infer<typeof usageKindSchema>;

/**
 * One line of « utilisé dans », link referents included.
 *
 * The same five fields as `versionedUsageSchema`, on purpose: §4 asks for « le
 * même affichage », so a link line renders through the very components a
 * protocol line renders through. `tags` on a link line is the tag its ENTRY
 * plays (« Lien « Campagne septembre » (production) »), where on a versioned
 * line it is where that referent's own pointers sit - two readings of one
 * field, and both answer « which version of the target this referent plays ».
 * `via` is always null for a link: a link names an experience directly, and it
 * is never an intermediary for anything.
 *
 * `playsNoyauAt`, since issue #154 (noyau.md §4.9, N37), is the one field a
 * line adds: the tags OF THE OBJECT ITSELF under which this referent plays the
 * noyau sequence with it - an experience whose tagged version carries a Noyau
 * block and cites the object, directly, under that tag. It is what lets a
 * protocol's save box and its production confirmation name the experiences a
 * version with no reading for the analyst would stop from starting, a
 * development save reading `development` and « Passer en production » reading
 * `production`. Computed by the server, which alone reads the referents'
 * versions; OPTIONAL, absent meaning none, so a line built by a reader that
 * does not know about the noyau - a fixture, a link line - stays a line.
 */
export const usageLineSchema = versionedUsageSchema.extend({
  kind: usageKindSchema,
  playsNoyauAt: z.array(versionTagSchema).optional(),
});
export type UsageLine = z.infer<typeof usageLineSchema>;

/**
 * What a CARD says about usage: a count per kind, never the list.
 *
 * The card is read-only and stays scannable (« Utilisé dans 2 protocoles et
 * 1 expérience »); the detail of who cites what is in the editor. A list of
 * pairs rather than a record keyed by kind, because a record over an enum is
 * partial in Zod and every reader would branch on absent keys - here an absent
 * kind is simply not in the list, and an empty list means « nulle part encore ».
 */
export const versionedUsageCountSchema = z
  .object({
    kind: versionedKindSchema,
    count: z.number().int().positive(),
  })
  .strict();
export type VersionedUsageCount = z.infer<typeof versionedUsageCountSchema>;

/**
 * The server draft's state, without its content: who touched it and when.
 *
 * The editor shows this at open time (« modifié par alice.pesty à 10:40 »), and
 * `updatedAt` is the token of the optimistic control: an autosave sends the
 * stamp of the draft it read, and the server refuses when its own is newer.
 * `null` means no draft: the editor opens on the development version.
 */
export const draftStateSchema = z
  .object({
    updatedAt: z.string().datetime(),
    updatedBy: z.string().min(1),
  })
  .strict();
export type DraftState = z.infer<typeof draftStateSchema>;

/**
 * The refusals the pattern can emit, as one closed catalogue.
 *
 * A screen has to word each of these differently - a taken name points at the
 * other entity, a stale draft offers to reload - so an opaque 409 would cost
 * the frontend a string match on a message. `no-change` is the one that keeps a
 * version empty of change from existing at all (§4).
 */
export const versionedErrorCodeSchema = z.enum([
  'name-taken',
  'draft-stale',
  'no-change',
  'retired',
  'pointer-empty',
  'version-unknown',
]);
export type VersionedErrorCode = z.infer<typeof versionedErrorCodeSchema>;

/**
 * The body of a refusal. `name` carries the other entity's name on
 * `name-taken`, so the screen names it rather than saying « déjà pris ».
 */
export const versionedErrorSchema = z
  .object({
    error: z.string().min(1),
    code: versionedErrorCodeSchema,
    name: z.string().optional(),
  })
  .strict();
export type VersionedError = z.infer<typeof versionedErrorSchema>;

/**
 * How long a version note may be. Named rather than inlined, because a SECOND
 * bound is computed from it: the MCP assistant forces every note it writes to
 * begin with « via Claude : » (§20.5, H39), so the note its tools accept is
 * this minus that prefix. Written as a literal in both places, the day one of
 * them moved the assistant would start posting notes this schema refuses.
 */
export const VERSION_NOTE_MAX = 200;

/** A version note. Short on purpose: it is one line of the history, not a changelog. */
export const versionNoteSchema = z.string().trim().max(VERSION_NOTE_MAX);

/**
 * A referent the save box ticked (vitrine.md §3.3, H22): the entity whose NEW
 * version takes the new version of the object being saved. The server reads
 * its plan again at the save and keeps only the referents it still finds in it,
 * so an identifier that is not a pinning referent is ignored, never written.
 */
export const referentRefSchema = z
  .object({
    kind: versionedKindSchema,
    id: z.string().min(1),
  })
  .strict();
export type ReferentRef = z.infer<typeof referentRefSchema>;

/**
 * « Enregistrer une nouvelle version » (§4). One request carries the two
 * gestures the box confirms together: the version, and optionally the move of
 * the production pointer onto it.
 *
 * `alsoProduction` defaults to false, and that default is the decision: the box
 * opens with the checkbox clear, so a save never moves production by accident.
 *
 * `updateReferents` is the third, since issue #192 (vitrine.md §3.3, H22): the
 * referents that pin an older version, whose new versions - made from their
 * « latest dev », with the one reference changed - are written in the SAME
 * transaction. Absent by default: a save changes nothing but its own object.
 */
export function saveVersionRequestSchema<Content extends z.ZodTypeAny>(content: Content) {
  return z
    .object({
      content,
      note: versionNoteSchema.optional(),
      alsoProduction: z.boolean().default(false),
      updateReferents: z.array(referentRefSchema).max(200).optional(),
    })
    .strict();
}

/**
 * An autosave of the draft. `baseUpdatedAt` is the stamp of the draft this
 * editor read - `null` when it read none - and the server compares it to its
 * own before writing. Last write wins, but only over a draft the writer had
 * actually seen.
 */
export function saveDraftRequestSchema<Content extends z.ZodTypeAny>(content: Content) {
  return z
    .object({
      content,
      baseUpdatedAt: z.string().datetime().nullable(),
    })
    .strict();
}

/**
 * The two request bodies as TYPES, for the generic editor that posts them
 * without knowing whose content it is carrying.
 *
 * Hand-written for the same reason as `VersionedDetail`: the builders above are
 * functions of a schema, so there is no call to take a `z.infer` of. `note` is
 * spelled `string | undefined` rather than `string` because that is what
 * `.optional()` infers, and `exactOptionalPropertyTypes` makes the two
 * different types.
 */
export type SaveVersionRequest<Content> = {
  content: Content;
  note?: string | undefined;
  alsoProduction: boolean;
  updateReferents?: ReferentRef[] | undefined;
};

export type SaveDraftRequest<DraftContent> = {
  content: DraftContent;
  baseUpdatedAt: string | null;
};

/** Moving one pointer onto one version: the rollback of §4, which copies nothing. */
export const movePointerRequestSchema = z
  .object({
    tag: versionTagSchema,
    versionId: z.string().min(1),
  })
  .strict();
export type MovePointerRequest = z.infer<typeof movePointerRequestSchema>;

/**
 * Everything the editor needs about one entity, in one read.
 *
 * `production` is nullable and `development` is not, and that asymmetry is the
 * §4 invariant made visible in the type: an entity is born with its v1, so
 * development always sits somewhere, while production waits for a gesture.
 *
 * `developmentContent` is what « Enregistrer » compares the draft against to
 * grey itself out. It is served beside the draft rather than fetched by a
 * second call, because the comparison is what decides whether the only button
 * of the screen is usable.
 *
 * TWO content schemas and not one, and the second is the whole point: a
 * version is always COMPLETE, a draft is what somebody is in the middle of
 * typing. Served under the complete schema, a draft whose direction is still
 * empty is a shape the route cannot type and `api.ts` cannot parse - the
 * editor would blow up on exactly the reload the draft exists to survive. So
 * `developmentContent` takes the version's schema and `draft.content` takes
 * the draft's, the same one `saveDraftRequestSchema` accepts.
 */
export function versionedDetailSchema<
  Content extends z.ZodTypeAny,
  DraftContent extends z.ZodTypeAny,
>(content: Content, draftContent: DraftContent) {
  return z
    .object({
      id: z.string().min(1),
      name: z.string().min(1),
      createdAt: z.string().datetime(),
      createdBy: z.string().min(1),
      retiredAt: z.string().datetime().nullable(),
      production: versionSummarySchema.nullable(),
      development: versionSummarySchema,
      versions: z.array(versionSummarySchema),
      developmentContent: content,
      /*
        Spelled out rather than `draftStateSchema.and(...)`: an intersection of
        two `.strict()` objects refuses every key of the other half, so the
        composed schema would reject both shapes it is made of.
      */
      draft: z
        .object({
          updatedAt: z.string().datetime(),
          updatedBy: z.string().min(1),
          content: draftContent,
        })
        .strict()
        .nullable(),
      usage: z.array(usageLineSchema),
    })
    .strict();
}

/**
 * The same shape as a TYPE, for the generic readers that take a detail without
 * knowing whose it is.
 *
 * Hand-written rather than inferred, because `versionedDetailSchema` is a
 * FUNCTION of two schemas and `z.infer` cannot be taken of a call that has not
 * happened. It mirrors the builder above field for field, and it is one screen
 * away from it so the pair stays legible: a field added there is added here in
 * the same edit.
 */
export type VersionedDetail<Content, DraftContent> = {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  retiredAt: string | null;
  production: VersionSummary | null;
  development: VersionSummary;
  versions: VersionSummary[];
  developmentContent: Content;
  draft: { updatedAt: string; updatedBy: string; content: DraftContent } | null;
  usage: UsageLine[];
};

/** One version read in full, for the « Voir » gesture of the history. */
export function versionedContentSchema<Content extends z.ZodTypeAny>(content: Content) {
  return z
    .object({
      version: versionSummarySchema,
      content,
    })
    .strict();
}

/** What « Voir » reads, as a type - the counterpart of `versionedContentSchema`. */
export type VersionedVersion<Content> = { version: VersionSummary; content: Content };

/**
 * Renaming: the one write that does NOT create a version (§4).
 *
 * The name lives on the entity and is copied into each version's content, so a
 * rename touches the entity and the draft, and the next saved version carries
 * the new name. What the scribe reads stays the name frozen in the resolved
 * version, never the current one.
 */
/**
 * The same body, on an object's OWN name field - and it is not a convenience.
 *
 * A rename writes its name onto the entity AND into the server draft, which is
 * read back through the object's `draftContent` schema. So a bound WIDER here
 * than the object's own accepts a name that its draft schema then refuses to
 * parse: the write is already committed, and every later read of that entity -
 * and of the whole list, which builds a record per entity - throws on a row no
 * screen can repair. The criterion never met it (both were 120); the persona's
 * 80 of H13 is the first bound narrower than the default above.
 *
 * Each object therefore hands the layer the schema built from its own field,
 * and the default stays for whoever has no narrower one.
 */
export function renameRequestSchemaFor<Name extends z.ZodTypeAny>(name: Name) {
  return z.object({ name }).strict();
}

export const renameRequestSchema = renameRequestSchemaFor(z.string().trim().min(1).max(120));
export type RenameRequest = z.infer<typeof renameRequestSchema>;

/* -------------------------------------------------------------- the cards */

/**
 * What a CARD of §4 says about an entity, minus whatever depends on its
 * meaning.
 *
 * A raw shape and not a schema, because every object's card is this plus its
 * own facets - `exampleCount` for a criterion, the presentation and the cran
 * for a persona - and a spread into `z.object({ ...versionedCardShape, ... })`
 * keeps the thirteen common fields declared once. The alternative, an
 * intersection of two objects, cannot be `.strict()` on the whole.
 *
 * The three fields a card cannot be scanned for are computed server side:
 * `hasPendingDraft` needs a comparison with the development version,
 * `developmentAhead` a comparison of the two pointers, `authors` a walk of the
 * history.
 */
export const versionedCardShape = {
  id: z.string().min(1),
  name: z.string().min(1),
  /** Null when nothing was ever passed: the pill is then ABSENT, never « v- » (§4). */
  production: versionSummarySchema.nullable(),
  development: versionSummarySchema,
  /** A count per kind, never the list of referents: the detail is in the editor. */
  usage: z.array(versionedUsageCountSchema),
  createdAt: z.string().datetime(),
  createdBy: z.string().min(1),
  lastVersionAt: z.string().datetime(),
  lastVersionBy: z.string().min(1),
  retiredAt: z.string().datetime().nullable(),
  hasPendingDraft: z.boolean(),
  developmentAhead: z.boolean(),
  /** Its creator plus every version's author. What the author filter offers. */
  authors: z.array(z.string().min(1)).min(1),
} as const;

export const versionedCardSchema = z.object(versionedCardShape).strict();
export type VersionedCard = z.infer<typeof versionedCardSchema>;

/* ------------------------------------------------------------ the filters */

/**
 * The tag filter (§4): « en production » and « jamais passé en production ».
 *
 * Two states and not three, because every entity has a development pointer by
 * construction - a « en développement » option would match everything and
 * teach an operator that the filter is broken.
 */
export const versionedTagFilterSchema = z.enum(['all', 'in-production', 'never-production']);
export type VersionedTagFilter = z.infer<typeof versionedTagFilterSchema>;

/**
 * The four sorts of §4 that hold for every object, in the order the select
 * lists them - the default first. Each object adds its own after them.
 */
export const versionedSortSchema = z.enum(['name', 'last-version', 'created', 'usage']);
export type VersionedSort = z.infer<typeof versionedSortSchema>;

/**
 * The default of every list: by name (§4, issue #113).
 *
 * It was the last version, most recent on top, until 2026-09-21. A library is
 * browsed by name, and an order that moves at every save is one an operator
 * has to read again each time they come back.
 */
export const VERSIONED_DEFAULT_SORT: VersionedSort = 'name';

/**
 * The filters §4 gives to the list of every versioned object, as a raw shape.
 *
 * Thirteen dimensions, each optional with a NEUTRAL default, so an absent
 * parameter and a reset filter are the same thing - which is what lets a
 * filtered list be pasted into a team thread as a short URL. The `sort` is not
 * here: it is the one common field whose enum grows per object, so each
 * object's schema spreads this shape and adds its own `sort`.
 */
export const versionedFiltersShape = {
  name: z.string().trim().default(''),
  tag: versionedTagFilterSchema.default('all'),
  /** A referent's entity id, or `nowhere` for the entities nothing cites. */
  protocolId: z.string().min(1).nullable().default(null),
  experienceId: z.string().min(1).nullable().default(null),
  /** Created OR wrote a version: one filter, because a v1 is a creation. */
  author: z.string().min(1).nullable().default(null),
  createdFrom: z.string().date().nullable().default(null),
  createdTo: z.string().date().nullable().default(null),
  versionFrom: z.string().date().nullable().default(null),
  versionTo: z.string().date().nullable().default(null),
  showRetired: z.boolean().default(false),
  unusedOnly: z.boolean().default(false),
  pendingDraftOnly: z.boolean().default(false),
  developmentAheadOnly: z.boolean().default(false),
} as const;

/**
 * Private, and only here to carry the type below: the neutral STATE of the
 * common dimensions belongs to each object, built from its own schema, and a
 * second one exported here would be a default two lots could disagree on.
 */
const versionedFiltersSchema = z
  .object({ ...versionedFiltersShape, sort: versionedSortSchema.default(VERSIONED_DEFAULT_SORT) })
  .strict();

/**
 * The total filter state a generic rule takes: the thirteen common dimensions
 * plus a sort the object names.
 *
 * Generic in the sort, and its default is the bare `string`: that is what makes
 * an object's own filter type - which carries extra dimensions AND a wider sort
 * enum - assignable to it. A constraint written with the narrow enum would have
 * refused every object that adds a sort of its own, which is all of them. The
 * layer reads only what is declared here and hands the rest back to the
 * object's extension.
 */
export type VersionedFilters<Sort extends string = string> = Omit<
  z.infer<typeof versionedFiltersSchema>,
  'sort'
> & { sort: Sort };
